/**
 * Formatting utility functions
 */

/**
 * Format currency amount
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date for display
 *
 * WP-BUG-BILLING-SYNC-2: Handles null/undefined dates gracefully.
 * Returns placeholder text instead of epoch date (Dec 31, 1969).
 */
export function formatDate(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  locale: string = 'en-US',
  placeholder: string = '—'
): string {
  if (!date) {
    return placeholder;
  }
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, options).format(d);
}

/**
 * Parse a date string as UTC if it doesn't have timezone info.
 * Backend timestamps are stored in UTC but may be returned without timezone suffix.
 */
export function parseAsUTC(date: string | Date): Date {
  if (date instanceof Date) {
    return date;
  }
  // If the string already has timezone info (Z or +/-offset), parse directly
  if (/[Zz]$/.test(date) || /[+-]\d{2}:\d{2}$/.test(date)) {
    return new Date(date);
  }
  // Otherwise, treat as UTC by appending 'Z'
  return new Date(date + 'Z');
}

/**
 * Format relative time (e.g., "2 hours ago")
 * Note: Timestamps from the backend are in UTC, so we parse them as UTC.
 */
export function formatRelativeTime(date: string | Date, locale: string = 'en-US'): string {
  const d = parseAsUTC(date);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second');
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return rtf.format(-diffInMinutes, 'minute');
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return rtf.format(-diffInHours, 'hour');
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return rtf.format(-diffInDays, 'day');
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return rtf.format(-diffInMonths, 'month');
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return rtf.format(-diffInYears, 'year');
}

/**
 * Format percentage
 */
export function formatPercent(
  value: number,
  decimals: number = 1,
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format account mask (last 4 digits)
 */
export function formatAccountMask(mask?: string): string {
  if (!mask) return '****';
  return `****${mask}`;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Capitalize first letter
 */
export function capitalize(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);

  // Use more precision for smaller values, less for larger
  const precision = value < 10 ? 2 : value < 100 ? 1 : 0;
  return `${value.toFixed(precision)} ${units[i]}`;
}

/**
 * Get human-readable file type label from MIME type
 */
export function getFileTypeLabel(mimeType: string): string {
  const typeMap: Record<string, string> = {
    'application/pdf': 'PDF',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/heic': 'HEIC',
    'text/csv': 'CSV',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/x-ofx': 'OFX',
    'application/x-qfx': 'QFX',
    'application/x-qif': 'QIF',
    'application/json': 'JSON',
    'application/zip': 'ZIP',
  };

  return typeMap[mimeType] || mimeType.split('/')[1]?.toUpperCase() || 'Unknown';
}

/**
 * Session timeout option type.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 */
export type SessionTimeoutOption = '15min' | '1hr' | '4hr' | '24hr' | '7d' | '30d';

/**
 * Mapping of session timeout options to milliseconds.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 */
const TIMEOUT_MS_MAP: Record<SessionTimeoutOption, number> = {
  '15min': 15 * 60 * 1000,        // 900,000 ms
  '1hr': 60 * 60 * 1000,          // 3,600,000 ms
  '4hr': 4 * 60 * 60 * 1000,      // 14,400,000 ms
  '24hr': 24 * 60 * 60 * 1000,    // 86,400,000 ms
  '7d': 7 * 24 * 60 * 60 * 1000,  // 604,800,000 ms
  '30d': 30 * 24 * 60 * 60 * 1000, // 2,592,000,000 ms
};

/**
 * Convert a session timeout option to milliseconds.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * @param option - The session timeout option string
 * @returns The timeout value in milliseconds
 */
export function sessionTimeoutToMs(option: SessionTimeoutOption): number {
  return TIMEOUT_MS_MAP[option] || TIMEOUT_MS_MAP['4hr']; // Default to 4 hours
}

/**
 * Calculate the warning threshold in milliseconds.
 * WP-SESSION-3: Warning appears at max(2 minutes, 10% of timeout).
 *
 * @param timeoutMs - The total timeout in milliseconds
 * @returns The warning threshold in milliseconds
 */
export function calculateWarningThreshold(timeoutMs: number): number {
  const twoMinutes = 2 * 60 * 1000;
  const tenPercent = timeoutMs * 0.1;
  return Math.max(twoMinutes, tenPercent);
}

// ============================================================================
// Currency Utilities for Retirement Planning (WP-RETIRE-2)
// ============================================================================

/**
 * Get currency symbol for a given currency code
 * Used by formatCurrencyCompact for manual formatting
 */
function getCurrencySymbol(currency: string, locale: string): string {
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).formatToParts(0);
  const symbolPart = parts.find((p) => p.type === 'currency');
  return symbolPart?.value ?? '$';
}

/**
 * Format currency in compact notation for large values
 * $1,200,000 -> $1.2M
 * $850,000 -> $850K
 * $45,000 -> $45K
 * $5,000 -> $5,000 (no abbreviation below $10K)
 *
 * NOTE: Uses manual formatting because Intl.NumberFormat 'compact' notation
 * doesn't combine well with currency style for our needs.
 *
 * @param amountCents - Amount in cents
 * @param currency - Currency code (default: USD)
 * @param locale - Locale for formatting (default: en-US)
 */
export function formatCurrencyCompact(
  amountCents: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  const dollars = amountCents / 100;
  const absValue = Math.abs(dollars);
  const sign = dollars < 0 ? '-' : '';
  const symbol = getCurrencySymbol(currency, locale);

  if (absValue >= 1_000_000) {
    // Millions: $1.2M
    const millions = absValue / 1_000_000;
    const formatted = millions.toFixed(1).replace(/\.0$/, ''); // Remove .0 if whole number
    return `${sign}${symbol}${formatted}M`;
  } else if (absValue >= 10_000) {
    // Thousands: $850K (no decimal for readability)
    const thousands = Math.round(absValue / 1_000);
    return `${sign}${symbol}${thousands}K`;
  } else {
    // Small values: use whole dollar formatting (no cents for cleaner display)
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(dollars));
  }
}

/**
 * Convert cents to dollars for display
 * @param cents - Amount in cents
 * @returns Amount in dollars
 */
export function centsToDollars(cents: number): number {
  return cents / 100;
}

/**
 * Convert dollars to cents for API
 * @param dollars - Amount in dollars
 * @returns Amount in cents (rounded to avoid floating point issues)
 */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}
